import React, { useEffect, useState, useCallback, useRef } from 'react'
import Unity, { UnityContext } from "react-unity-webgl"

const PHOTON_APP_ID = '92bb1ed8-0330-4698-a3cf-1d4fc621ada6'

const unityContext = new UnityContext({
    loaderUrl:    "PoolBuild/8-Ball-Pool.loader.js",
    dataUrl:      "PoolBuild/8-Ball-Pool.data",
    frameworkUrl: "PoolBuild/8-Ball-Pool.framework.js",
    codeUrl:      "PoolBuild/8-Ball-Pool.wasm",
})

const PoolGame = () => {
    const [progress, setProgress]   = useState(0)
    const [loaded, setLoaded]       = useState(false)
    const [status, setStatus]       = useState('Loading...')
    const [matchInfo, setMatchInfo] = useState(null)
    const [winner, setWinner]       = useState(null)
    const [isFullscreen, setIsFullscreen] = useState(false)
    const injectedRef   = useRef(false)
    const canvasWrapRef = useRef(null)

    const getWalletAddress = useCallback(async () => {
        try {
            if (window.ethereum) {
                const accounts = await window.ethereum.request({ method: 'eth_accounts' })
                if (accounts && accounts.length > 0) return accounts[0]
                const newAccounts = await window.ethereum.request({ method: 'eth_requestAccounts' })
                return newAccounts[0]
            }
        } catch (err) { console.error('Wallet error:', err) }
        return localStorage.getItem('walletAddress') || '0x0000000000000000000000000000000000000000'
    }, [])

    const interceptPhoton = useCallback(() => {
        if (injectedRef.current) return
        injectedRef.current = true
        const OriginalWebSocket = window.WebSocket
        window.WebSocket = function(url, protocols) {
            console.log('🔌 WebSocket connecting to:', url)
            if (url && (url.includes('photon') || url.includes('exitgames'))) {
                if (!url.includes(PHOTON_APP_ID)) {
                    url = url.replace(/AppId=[^&]*/, `AppId=${PHOTON_APP_ID}`)
                    console.log('✅ Injected App ID into Photon URL:', url)
                }
            }
            return new OriginalWebSocket(url, protocols)
        }
        window.WebSocket.prototype = OriginalWebSocket.prototype
        Object.defineProperty(window.WebSocket, 'CONNECTING', { value: 0 })
        Object.defineProperty(window.WebSocket, 'OPEN',       { value: 1 })
        Object.defineProperty(window.WebSocket, 'CLOSING',    { value: 2 })
        Object.defineProperty(window.WebSocket, 'CLOSED',     { value: 3 })
        console.log('🔧 Photon interceptor active:', PHOTON_APP_ID)
    }, [])

    const injectViaUnityConfig = useCallback(() => {
        const paths = [
            () => window.PhotonAppSettings,
            () => window.PhotonNetwork && window.PhotonNetwork.PhotonServerSettings,
            () => window.ServerSettings,
        ]
        for (const getter of paths) {
            try {
                const obj = getter()
                if (obj && obj.AppIdRealtime !== undefined) {
                    obj.AppIdRealtime = PHOTON_APP_ID
                    return true
                }
            } catch(e) {}
        }
        return false
    }, [])

    const handleFullscreen = useCallback(() => {
        const el = canvasWrapRef.current
        if (!el) return
        if (!document.fullscreenElement && !document.webkitFullscreenElement) {
            const canvas = el.querySelector("canvas"); const target = canvas || el; const req = target.requestFullscreen || target.webkitRequestFullscreen || target.mozRequestFullScreen
            if (req) req.call(target).then(() => setIsFullscreen(true)).catch(console.error)
        } else {
            const exit = document.exitFullscreen || document.webkitExitFullscreen
            if (exit) exit.call(document).then(() => setIsFullscreen(false)).catch(console.error)
        }
    }, [])

    useEffect(() => {
        const onChange = () => setIsFullscreen(!!(document.fullscreenElement || document.webkitFullscreenElement))
        document.addEventListener('fullscreenchange', onChange)
        document.addEventListener('webkitfullscreenchange', onChange)
        return () => {
            document.removeEventListener('fullscreenchange', onChange)
            document.removeEventListener('webkitfullscreenchange', onChange)
        }
    }, [])

    useEffect(() => {
        interceptPhoton()
        unityContext.on('progress', (p) => { setProgress(p); setStatus(`Loading... ${Math.round(p * 100)}%`) })
        unityContext.on('loaded', () => {
            setLoaded(true); setStatus('Connected')
            setTimeout(injectViaUnityConfig, 500)
            setTimeout(injectViaUnityConfig, 2000)
        })
        unityContext.on('Request_WalletAddress', async (receiverObject) => {
            const address = await getWalletAddress()
            unityContext.send(receiverObject, 'Received_WalletAddress', address)
        })
        unityContext.on('Match_Joined', (matchID, playerWalletAddress) => {
            setMatchInfo({ matchID, playerWalletAddress }); setStatus('In Match')
        })
        unityContext.on('Match_End', (matchID, winnerWalletAddress) => {
            setWinner({ matchID, winnerWalletAddress }); setStatus('Match Over')
        })
        unityContext.on('GameOver', (finalScore) => console.log('🎱 Game over:', finalScore))
        return () => unityContext.removeAllEventListeners()
    }, [getWalletAddress, interceptPhoton, injectViaUnityConfig])

    return (
        <>
            <div className="cs-root">
                <div className="cs-header">
                    <div className="cs-logo">
                        <span className="cs-logo-icon">♣</span>
                        <span className="cs-logo-text">ClubSwap</span>
                    </div>
                    <div className="cs-title">8 Ball Pool</div>
                    <div className="cs-status">
                        <span className={`cs-dot ${loaded ? 'on' : 'loading'}`} />
                        <span className="cs-status-text">{status}</span>
                    </div>
                </div>

                {!loaded && (
                    <div className="cs-loadbar-wrap">
                        <div className="cs-loadbar">
                            <div className="cs-loadfill" style={{ width: `${progress * 100}%` }} />
                        </div>
                        <div className="cs-loadpct">{Math.round(progress * 100)}%</div>
                    </div>
                )}

                <div className="cs-canvas-wrap" ref={canvasWrapRef}>
                    <Unity unityContext={unityContext} />
                    <button className="cs-fs-btn" onClick={handleFullscreen} title={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}>
                        {isFullscreen ? '✕' : '⛶'}
                    </button>
                    {matchInfo && <div className="cs-hud-match">🎱 {matchInfo.matchID?.slice(0,18)}...</div>}
                    {winner && <div className="cs-hud-winner">🏆 Winner: {winner.winnerWalletAddress?.slice(0,10)}...</div>}
                </div>

                <div className="cs-footer">© 2024 ClubSwap · All rights reserved</div>
            </div>

            <style jsx>{`
                *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
                .cs-root { min-height: 100vh; background: #050a0f; display: flex; flex-direction: column; align-items: center; font-family: 'Segoe UI', system-ui, sans-serif; color: #fff; }
                .cs-header { width: 100%; display: flex; align-items: center; justify-content: space-between; padding: 12px 24px; background: rgba(0,0,0,0.7); border-bottom: 1px solid #0d2033; flex-wrap: wrap; gap: 8px; }
                .cs-logo { display: flex; align-items: center; gap: 8px; }
                .cs-logo-icon { font-size: 26px; color: #00bfff; }
                .cs-logo-text { font-size: 20px; font-weight: 800; letter-spacing: 0.05em; background: linear-gradient(135deg, #00bfff, #0070cc); -webkit-background-clip: text; -webkit-text-fill-color: transparent; }
                .cs-title { font-size: 17px; font-weight: 700; color: #cce8ff; letter-spacing: 0.1em; text-transform: uppercase; }
                .cs-status { display: flex; align-items: center; gap: 6px; }
                .cs-dot { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; }
                .cs-dot.on { background: #00ff88; }
                .cs-dot.loading { background: #ffaa00; animation: blink 1s infinite; }
                @keyframes blink { 0%,100%{opacity:1} 50%{opacity:0.2} }
                .cs-status-text { font-size: 13px; color: #778899; }
                .cs-loadbar-wrap { width: 100%; max-width: 480px; padding: 20px 20px 0; display: flex; flex-direction: column; align-items: center; gap: 6px; }
                .cs-loadbar { width: 100%; height: 5px; background: #0d1e2e; border-radius: 3px; overflow: hidden; }
                .cs-loadfill { height: 100%; background: linear-gradient(90deg, #0060bb, #00bfff); border-radius: 3px; transition: width 0.3s ease; }
                .cs-loadpct { font-size: 12px; color: #445566; }
                .cs-canvas-wrap { position: relative; width: 100%; max-width: 960px; margin: 16px auto; padding: 0 16px; display: flex; justify-content: center; }
                .cs-canvas-wrap canvas { width: 100% !important; max-width: 100%; height: auto !important; aspect-ratio: 16/9; display: block; background: #000; border-radius: 8px; box-shadow: 0 0 48px rgba(0,100,220,0.18); }
                .cs-canvas-wrap:fullscreen { width: 100vw; height: 100vh; max-width: none; padding: 0; background: #000; display: flex; align-items: center; justify-content: center; }
                .cs-canvas-wrap:fullscreen canvas { width: 100vw !important; height: 100vh !important; max-width: none !important; border-radius: 0 !important; aspect-ratio: unset; }
                .cs-canvas-wrap:-webkit-full-screen { width: 100vw; height: 100vh; max-width: none; padding: 0; background: #000; display: flex; align-items: center; justify-content: center; }
                .cs-canvas-wrap:-webkit-full-screen canvas { width: 100vw !important; height: 100vh !important; max-width: none !important; border-radius: 0 !important; aspect-ratio: unset; }
                .cs-fs-btn { position: absolute; bottom: 14px; right: 24px; width: 38px; height: 38px; background: rgba(0,0,0,0.6); border: 1px solid rgba(0,150,255,0.35); border-radius: 6px; color: #88bbdd; font-size: 19px; cursor: pointer; display: flex; align-items: center; justify-content: center; transition: all 0.15s; z-index: 20; padding: 0; }
                .cs-fs-btn:hover { background: rgba(0,70,170,0.55); border-color: #00bfff; color: #fff; }
                .cs-hud-match { position: absolute; top: 10px; left: 24px; background: rgba(0,0,0,0.6); color: #99bbdd; font-size: 11px; padding: 3px 8px; border-radius: 4px; border: 1px solid #0d3050; pointer-events: none; }
                .cs-hud-winner { position: absolute; top: 50%; left: 50%; transform: translate(-50%,-50%); background: rgba(0,0,0,0.9); color: gold; font-size: 22px; font-weight: 700; padding: 18px 32px; border-radius: 10px; border: 2px solid gold; text-align: center; pointer-events: none; }
                .cs-footer { margin-top: auto; padding: 16px; font-size: 11px; color: #2a3a4a; letter-spacing: 0.05em; }
                @media (max-width: 600px) { .cs-header { padding: 10px 14px; } .cs-logo-text { font-size: 16px; } .cs-title { font-size: 13px; } .cs-canvas-wrap { padding: 0 8px; margin: 10px auto; } .cs-fs-btn { bottom: 10px; right: 14px; width: 32px; height: 32px; font-size: 16px; } }
            `}</style>
        </>
    )
}

export default PoolGame
